import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";

import { ApiError } from "@/react/config/CustomErrors";
import type { Comment } from "@/react/types/Comment";

import { COMMENTS_BASE_QUERY_KEY } from "../CommentsList/getCommentsListKey";

const COMMENTS_HIERARCHY_QUERY_KEY = "hierarchy";

export const getCommentHierarchyKey = ({
  postId,
  id,
}: {
  postId?: number | null;
  id: string | number;
}) => [
  COMMENTS_BASE_QUERY_KEY,
  postId,
  COMMENTS_HIERARCHY_QUERY_KEY,
  String(id),
];

export const useCommentHierarchy = ({
  postId,
  id,
  postCommentsClosed,
  postLikingDisabled,
}: {
  postId: number;
  id: number;
  postCommentsClosed: boolean;
  postLikingDisabled: boolean;
}) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => getCommentHierarchyKey({ postId, id }),
    [postId, id],
  );

  useEffect(() => {
    queryClient.removeQueries({ queryKey });
  }, [postCommentsClosed, postLikingDisabled, queryClient, queryKey]);

  // `gcTime: 0` ensures that comments are always fully reloaded on every
  // mount, i.e., whenever the comment section is opened. This is done to cope
  // with the current comment highlighting and anchor link behavior.
  const { data: comment, isPending } = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        return await reactQueryGet(
          internalApi.comments.commentHierarchy({
            postId: postId,
            id: id ?? undefined,
          }),
        );
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    gcTime: 0,
    retry(failureCount, error: ApiError) {
      // When moderators reject a reported comment, the comment is soft-deleted
      // and the refetch returns a 404. We don't want to retry in this case.
      if (error.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const updateQueryData = (commentData: Comment) =>
    queryClient.setQueryData(queryKey, {
      ...commentData,
      replies: comment.replies,
    });

  return {
    comment,
    isPending,
    updateQueryData,
  };
};
